import axios, {AxiosError, AxiosInstance, AxiosRequestConfig, HttpStatusCode, InternalAxiosRequestConfig} from "axios";
import {AuthenticationRepository} from "./AuthenticationRepository.ts";
import {firstValueFrom} from "rxjs";
import HTTPConverter from "./converters/HTTPConverter.ts";
import generalErrorConverter from "./converters/GeneralErrorConverter.ts";
import {ActivationRepositoryType} from "./interfaces/ActivationRepositoryType.ts";
import {
    ActivationCommand,
    ActivationDetail,
    ActivationPolicy,
    ActivationsPage,
    ActivationsQuery,
    ActivationView,
    AgencyActivationSettings,
    UpdateActivationTravelerPayload,
} from "../domain/entities/Activation.ts";

// TripBook Activation endpoints (51trips-colab-api, contract §3). Bearer
// Firebase token; an agency only sees its own activations, an admin passes
// ?agencyId=.
export class ActivationRepository implements ActivationRepositoryType {

    client: AxiosInstance

    get serverBaseURL(): string {
        return import.meta.env.VITE_APP_SERVER_URL
    }

    constructor(authenticationRepository: AuthenticationRepository) {
        this.client = axios.create({
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            timeout: 40000,
            validateStatus: (status: number) => status >= 200 && status < 300
        })

        this.client.interceptors.request.use(
            async (
                config: InternalAxiosRequestConfig
            ): Promise<InternalAxiosRequestConfig & { retry: number }> => {
                const token = await firstValueFrom(authenticationRepository.getIDToken())
                config.headers.set('Authorization', `Bearer ${token}`)

                const configWithRetry = config as InternalAxiosRequestConfig & {
                    retry: number
                }
                return { ...config, retry: configWithRetry.retry === 0 ? 0 : 1 }
            }
        )

        this.client.interceptors.response.use(
            (response) => HTTPConverter.fromResponse(response),
            async (err: AxiosError) => {
                const config = err.config as AxiosRequestConfig & { retry: number }
                if (err?.response?.status === HttpStatusCode.Unauthorized && config && config.retry > 0) {
                    config.retry = 0
                    return this.client.request(config)
                }
                // Throw (as TripbookRepository does) so React Query mutations
                // see the failure and callers can read errorKey via parseBackendError.
                throw generalErrorConverter.fromBackendServerError(err)
            }
        )
    }

    getActivationPolicy(market: string): Promise<ActivationPolicy> {
        return this.client.get(`${this.serverBaseURL}/activation-policies/${encodeURIComponent(market)}`)
    }

    getActivations({notActivated, ...query}: ActivationsQuery): Promise<ActivationsPage> {
        return this.client.get(`${this.serverBaseURL}/activations`, {
            params: {...query, notActivated: notActivated ? 'true' : undefined},
        })
    }

    getActivation(id: string, agencyId?: string): Promise<ActivationDetail> {
        return this.client.get(`${this.serverBaseURL}/activations/${id}`, {params: {agencyId}})
    }

    updateActivationTraveler(id: string, payload: UpdateActivationTravelerPayload, agencyId?: string): Promise<ActivationView> {
        return this.client.patch(`${this.serverBaseURL}/activations/${id}/traveler`, payload, {params: {agencyId}})
    }

    sendActivationCommand(id: string, command: ActivationCommand, agencyId?: string): Promise<ActivationView | void> {
        return this.client.post(`${this.serverBaseURL}/activations/${id}/commands`, command, {params: {agencyId}})
    }

    updateMyActivationSettings(settings: AgencyActivationSettings): Promise<void> {
        return this.client.patch(`${this.serverBaseURL}/agencies/me/activation-settings`, settings)
    }
}
